// Página de galeria do evento
import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'wouter';
import { ShoppingCart, Download, Search, Grid, List } from 'lucide-react';
import { formatSimpleDate } from '@/utils/dateUtils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useCartContext } from '@/contexts/CartContext';
import { useUser } from '@/contexts/UserContext';
import { useEvent } from '../hooks/useEvents';
import { api } from '@/services/axios';

interface Photo {
    id: string;
    eventId: string;
    photographerId: string;
    filename: string;
    originalName: string;
    mimeType: string;
    size: number;
    uploadedAt: string;
    tags?: string[];
    price: number;
    isApproved: boolean;
    thumbnailUrl: string;
    fullUrl: string;
    pricePerPhoto?: number;
    eventTitle?: string;
    monetizationType?: 'free' | 'sale';
    photographerName?: string;
}

const EventGallery: React.FC = () => {
    const { eventId } = useParams();
    const [, setLocation] = useLocation();
    const { addToCart, isLoading: cartLoading } = useCartContext();
    const { isLoggedIn } = useUser();

    // Usar o hook do módulo Events
    const { data: event, isLoading: eventLoading } = useEvent(eventId!);

    const [photos, setPhotos] = useState<Photo[]>([]);
    const [filteredPhotos, setFilteredPhotos] = useState<Photo[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
    const [availableTags, setAvailableTags] = useState<string[]>([]);

    useEffect(() => {
        if (eventId) {
            fetchPhotos();
        }
    }, [eventId]);

    useEffect(() => {
        filterPhotos();
    }, [photos, searchTerm, selectedTags]);

    const fetchPhotos = async () => {
        try {
            setLoading(true);

            const response = await api.get(`/api/photos?eventId=${eventId}`);

            const apiResponse = response.data || {};
            const photosData = apiResponse.data?.photos || apiResponse.photos || [];

            // Mapear dados usando campos reais do banco de dados
            const mappedPhotos = photosData.map((apiPhoto: any) => {
                return {
                    id: apiPhoto.id,
                    eventId: apiPhoto.eventId,
                    photographerId: apiPhoto.photographerId,
                    filename: apiPhoto.filename,
                    originalName: apiPhoto.filename || 'Foto sem nome',
                    mimeType: 'image/jpeg',
                    size: 0,
                    uploadedAt: apiPhoto.uploadedat || apiPhoto.uploadedAt,
                    tags: [],
                    price: apiPhoto.pricePerPhoto || 0,
                    isApproved: apiPhoto.visibility === 'Public',
                    thumbnailUrl: apiPhoto.thumbnailurl || apiPhoto.thumbnailUrl,
                    fullUrl: apiPhoto.storageurl || apiPhoto.storageUrl,
                    pricePerPhoto: apiPhoto.pricePerPhoto,
                    eventTitle: apiPhoto.eventTitle,
                    monetizationType: apiPhoto.monetizationType || 'sale',
                    photographerName: apiPhoto.photographerName || 'Fotógrafo não identificado'
                };
            });

            // Filtrar apenas fotos aprovadas (públicas)
            const approvedPhotos = mappedPhotos.filter((photo: Photo) => photo.isApproved);
            setPhotos(approvedPhotos);

            // Extrair tags únicas
            const tags = new Set<string>();
            approvedPhotos.forEach((photo: Photo) => {
                photo.tags?.forEach(tag => tags.add(tag));
            });
            setAvailableTags(Array.from(tags));

        } catch (err) {
            setError('Erro ao carregar fotos do evento');
            console.error('Error fetching photos:', err);
        } finally {
            setLoading(false);
        }
    };

    const filterPhotos = () => {
        let filtered = photos;

        if (searchTerm) {
            filtered = filtered.filter(photo =>
                photo.originalName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                photo.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
            );
        }

        if (selectedTags.length > 0) {
            filtered = filtered.filter(photo =>
                selectedTags.every(tag => photo.tags?.includes(tag))
            );
        }

        setFilteredPhotos(filtered);
    };


    const handleAddToCart = async (photo: Photo) => {
        if (!isLoggedIn) {
            setLocation('/login');
            return;
        }

        if (photo.monetizationType === 'free') {
            return;
        }

        try {
            await addToCart(photo.id, 1);
        } catch (error) {
            console.error('Erro ao adicionar ao carrinho:', error);
        }
    };

    const handleDownload = async (photo: Photo) => {
        if (photo.monetizationType === 'free') {
            const link = document.createElement('a');
            link.href = photo.fullUrl || photo.thumbnailUrl;
            link.download = photo.originalName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const toggleTag = (tag: string) => {
        setSelectedTags(prev =>
            prev.includes(tag)
                ? prev.filter(t => t !== tag)
                : [...prev, tag]
        );
    };

    if (loading || eventLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Carregando galeria...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <p className="text-red-600 mb-4">{error}</p>
                    <Button onClick={() => setLocation('/')} variant="outline">
                        Voltar ao Início
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header do Evento */}
            <div className="bg-white shadow-sm border-b">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">{event?.title}</h1>
                            <p className="text-gray-600 mt-1">{event?.description}</p>
                            <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                                <span>📅 {event?.date && formatSimpleDate(event.date)}</span>
                                <span>📍 {event?.location}</span>
                                <Badge variant="secondary">{event?.tags}</Badge>
                            </div>
                        </div>
                        <Button onClick={() => setLocation('/')} variant="outline">
                            Voltar
                        </Button>
                    </div>
                </div>
            </div>

            {/* Filtros e Busca */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
                    <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
                        <div className="flex-1 max-w-md">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                                <Input
                                    placeholder="Buscar fotos..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10"
                                />
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            <Button
                                variant={viewMode === 'grid' ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => setViewMode('grid')}
                            >
                                <Grid className="h-4 w-4" />
                            </Button>
                            <Button
                                variant={viewMode === 'list' ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => setViewMode('list')}
                            >
                                <List className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>

                    {/* Tags */}
                    {availableTags.length > 0 && (
                        <div className="mt-4">
                            <p className="text-sm font-medium text-gray-700 mb-2">Filtrar por tags:</p>
                            <div className="flex flex-wrap gap-2">
                                {availableTags.map(tag => (
                                    <Badge
                                        key={tag}
                                        variant={selectedTags.includes(tag) ? 'default' : 'secondary'}
                                        className="cursor-pointer hover:bg-blue-100"
                                        onClick={() => toggleTag(tag)}
                                    >
                                        {tag}
                                    </Badge>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Galeria de Fotos */}
                <div className="mb-4">
                    <p className="text-gray-600">
                        {filteredPhotos.length} foto{filteredPhotos.length !== 1 ? 's' : ''} encontrada{filteredPhotos.length !== 1 ? 's' : ''}
                    </p>
                </div>

                {filteredPhotos.length === 0 ? (
                    <div className="text-center py-12">
                        <p className="text-gray-500 text-lg">Nenhuma foto encontrada</p>
                        <p className="text-gray-400 mt-2">Tente ajustar os filtros de busca</p>
                    </div>
                ) : (
                    <div className={`grid gap-6 ${viewMode === 'grid'
                        ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
                        : 'grid-cols-1'
                        }`}>
                        {filteredPhotos.map((photo) => (
                            <Card key={photo.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                                <div className="relative group">
                                    <img
                                        src={photo.thumbnailUrl}
                                        alt={photo.originalName}
                                        className="w-full h-64 object-cover"
                                        onError={(e) => {
                                            console.error('Erro ao carregar imagem da galeria:', photo);
                                            e.currentTarget.src = 'https://via.placeholder.com/300x300?text=Imagem+Indisponível';
                                        }}
                                    />

                                    {/* Overlay com ações */}
                                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 flex items-center justify-center">
                                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex gap-2">
                                            {photo.monetizationType === 'free' ? (
                                                <Button
                                                    size="sm"
                                                    onClick={() => handleDownload(photo)}
                                                    className="bg-green-600 hover:bg-green-700"
                                                >
                                                    <Download className="h-4 w-4" />
                                                </Button>
                                            ) : photo.monetizationType === 'sale' && photo.price > 0 ? (
                                                <Button
                                                    size="sm"
                                                    onClick={() => handleAddToCart(photo)}
                                                    disabled={cartLoading || !isLoggedIn}
                                                >
                                                    <ShoppingCart className="h-4 w-4" />
                                                </Button>
                                            ) : null}
                                        </div>
                                    </div>
                                </div>

                                <CardContent className="p-4">
                                    <div className="space-y-2">
                                        <div className="text-sm text-gray-500">
                                            <p>Cadastrada em: {formatSimpleDate(photo.uploadedAt)}</p>
                                            <p>Fotógrafo: {photo.photographerName || 'Não informado'}</p>
                                        </div>
                                        {(photo.monetizationType === 'free' || (photo.monetizationType === 'sale' && photo.price > 0)) && (
                                            <div className="flex items-center justify-between mt-2">
                                                {photo.monetizationType === 'free' ? (
                                                    <span className="text-lg font-bold text-green-600">
                                                        Gratuita
                                                    </span>
                                                ) : (
                                                    <span className="text-lg font-bold text-blue-600">
                                                        R$ {photo.price.toFixed(2)}
                                                    </span>
                                                )}
                                                {photo.monetizationType === 'free' ? (
                                                    <Button
                                                        size="sm"
                                                        onClick={() => handleDownload(photo)}
                                                        className="bg-green-600 hover:bg-green-700"
                                                    >
                                                        <Download className="h-4 w-4 mr-1" />
                                                        Download
                                                    </Button>
                                                ) : (
                                                    <Button
                                                        size="sm"
                                                        onClick={() => handleAddToCart(photo)}
                                                        disabled={cartLoading || !isLoggedIn}
                                                    >
                                                        {isLoggedIn ? 'Comprar' : 'Login para Comprar'}
                                                    </Button>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {photo.tags && photo.tags.length > 0 && (
                                        <div className="flex flex-wrap gap-1 mt-2">
                                            {photo.tags.slice(0, 3).map(tag => (
                                                <Badge key={tag} variant="outline" className="text-xs">
                                                    {tag}
                                                </Badge>
                                            ))}
                                            {photo.tags.length > 3 && (
                                                <Badge variant="outline" className="text-xs">
                                                    +{photo.tags.length - 3}
                                                </Badge>
                                            )}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default EventGallery;

